/**
 * Wellness Authentication Service
 *
 * Handles passwordless magic-link authentication for participant users.
 * Supports both legacy users from SurveyParticipants table and new B2C self-signup users.
 */

import { WellnessUser } from '../types';
import { getWellnessState } from '@/stores/wellness-store';
import { getTokenExpiration, calculateExpirationTime } from '../utils/jwtUtils';
import type { WellnessTokens } from '@/types/wellness';
import { queryClient } from '@/app/providers/QueryProvider';
import { identifyWellnessUser } from '@/utils/authUtils';

const API_URL = import.meta.env.VITE_REACT_APP_API_URL || '';

export interface LoginIntentRequest {
  email: string;
  parentSurveyAuthCode?: string;
  b2c?: boolean;
}

export class B2CAccountMissingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'B2CAccountMissingError';
  }
}

export interface LoginIntentResponse {
  session: string;
  challengeName: string;
  ttlSeconds: number;
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string; // YYYY-MM-DD format
  addToSurvey?: string; // Optional: survey ID for self-registration
  orgId?: string; // B2B2C sponsor org
  additionalChildSlots?: number; // Extra child slots beyond Family's 3 included
  /** UFC only: allowlisted suffix for HubSpot lead_source → sponsor:ufc:<detail> */
  leadSourceDetail?: 'mentoring' | 'clinical' | 'general';
}

export interface CreateUserResponse {
  session: string;
  challengeName: string;
  ttlSeconds: number;
  user?: {
    wasCreated: boolean;
  };
  participant?: {
    surveyParticipantId: string;
    surveyId: string;
    participantStatus: string;
    participantSurveyAuthCode?: string;
    parentSurveyAuthCode?: string;
    wasCreated: boolean;
  };
}

export interface VerifyRequest {
  email: string;
  token: string;
  session: string;
}

export interface VerifyResponse {
  accessToken: string;
  idToken: string;
  /** Not returned by `/self/auth/support-exchange` (bounded session; no silent refresh). */
  refreshToken?: string;
  expiresIn: number;
  tokenType: string;
  user: WellnessUser;
  /** When set by verify / support-exchange, preferred post-login route */
  redirectPath?: string;
}

export interface CheckSurveyEligibilityResponse {
  surveyName: string;
  orgLogo?: string; // base64 encoded PNG
  status: number;
  message: string;
}

/**
 * Persist Cognito tokens and user from verify / debug / support-exchange responses.
 */
function applyVerifyResponse(data: VerifyResponse): void {
  const idTokenExpiration = getTokenExpiration(data.idToken);
  const idTokenExpiresAt = idTokenExpiration
    ? idTokenExpiration.getTime()
    : calculateExpirationTime(data.expiresIn);

  const refreshTokenExpiration = data.refreshToken
    ? getTokenExpiration(data.refreshToken)
    : null;
  const refreshTokenExpiresAt = refreshTokenExpiration
    ? refreshTokenExpiration.getTime()
    : undefined;

  const tokens: WellnessTokens = {
    accessToken: data.accessToken,
    idToken: data.idToken,
    idTokenExpiresAt,
    ...(data.refreshToken
      ? {
          refreshToken: data.refreshToken,
          refreshTokenExpiresAt,
        }
      : {}),
  };

  const { setAuthData } = getWellnessState();
  setAuthData({
    tokens,
    user: data.user,
  });

  identifyWellnessUser(data.idToken, {
    email: data.user.email,
    b2c: data.user.b2c,
    needsProfile: data.user.needsProfile,
  });
}

const supportExchangeInFlight = new Map<string, Promise<VerifyResponse>>();

/**
 * Support staff "login as" — exchange one-time code from B2B bootstrap for participant tokens.
 * Deduplicates concurrent calls with the same code (e.g. React StrictMode).
 */
export async function supportExchangeDeduped(code: string): Promise<VerifyResponse> {
  const trimmed = code.trim();
  let p = supportExchangeInFlight.get(trimmed);
  if (p) {
    return p;
  }
  p = supportExchange(trimmed).finally(() => {
    supportExchangeInFlight.delete(trimmed);
  });
  supportExchangeInFlight.set(trimmed, p);
  return p;
}

async function supportExchange(code: string): Promise<VerifyResponse> {
  const response = await fetch(`${API_URL}/self/auth/support-exchange`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  const errorData = await response.json().catch(() => ({}));

  if (!response.ok) {
    if (response.status === 503) {
      throw new Error(
        typeof errorData.message === 'string'
          ? errorData.message
          : 'Support sign-in is temporarily unavailable. Please try again later.'
      );
    }
    throw new Error(
      errorData.message || `Support sign-in failed: ${response.statusText}`
    );
  }

  const data: VerifyResponse = errorData as VerifyResponse;
  applyVerifyResponse(data);
  getWellnessState().setSupportSession(true);
  return data;
}

/**
 * Clear auth and go to wellness login (shared by user menu and support-session banner).
 */
export function logoutWellnessAndNavigate(
  navigate: (to: string, options?: { replace?: boolean }) => void
): void {
  clearAuth();
  clearRedirectPath();
  navigate('/wellness/login', { replace: true });
}

/**
 * Store session from login-intent or create-user for later use in verify step
 */
export const storeSession = (session: string): void => {
  const { setSession } = getWellnessState();
  setSession(session);
};

/**
 * Retrieve stored session
 */
export const getStoredSession = (): string | null => {
  return getWellnessState().session;
};

/**
 * Clear stored session
 */
export const clearSession = (): void => {
  const { setSession, setEmail } = getWellnessState();
  setSession(null);
  setEmail(null);
};

/**
 * Store email from login-intent or create-user for later use in verify step
 */
export const storeEmail = (email: string): void => {
  const { setEmail } = getWellnessState();
  setEmail(email);
};

/**
 * Retrieve stored email
 */
export const getStoredEmail = (): string | null => {
  return getWellnessState().email;
};

/**
 * Clear stored email
 */
export const clearEmail = (): void => {
  const { setEmail } = getWellnessState();
  setEmail(null);
};

/**
 * Store redirect path for post-authentication redirect
 */
export const storeRedirectPath = (redirectPath: string): void => {
  const { setRedirectPath } = getWellnessState();
  setRedirectPath(redirectPath);
};

/**
 * Retrieve stored redirect path
 */
export const getStoredRedirectPath = (): string | null => {
  return getWellnessState().redirectPath;
};

/**
 * Clear stored redirect path
 */
export const clearRedirectPath = (): void => {
  const { setRedirectPath } = getWellnessState();
  setRedirectPath(null);
};

/**
 * Get idToken for Authorization header
 * Returns "Bearer {idToken}" or null if not authenticated
 */
export const getAuthHeader = (): string | null => {
  const idToken = getWellnessState().tokens?.idToken ?? null;
  return idToken ? `Bearer ${idToken}` : null;
};

/**
 * Get stored idToken
 */
export const getIdToken = (): string | null => {
  return getWellnessState().tokens?.idToken ?? null;
};

/**
 * Get stored accessToken
 */
export const getAccessToken = (): string | null => {
  return getWellnessState().tokens?.accessToken ?? null;
};

/**
 * Get stored refreshToken
 */
export const getRefreshToken = (): string | null => {
  return getWellnessState().tokens?.refreshToken ?? null;
};

/**
 * Check if user is authenticated (has idToken)
 */
export const isAuthenticated = (): boolean => {
  return !!getIdToken();
};

/**
 * Clear all stored tokens and participant data
 */
export const clearAuth = (): void => {
  const { clearAuth: resetAuth } = getWellnessState();
  resetAuth();
  // Invalidate all wellness participant queries first
  queryClient.invalidateQueries({ queryKey: ['wellness', 'participants'] });
  // Clear React Query cache
  queryClient.clear();
  // Clear persisted wellness store from localStorage
  localStorage.removeItem('wellness-store');
};

/**
 * Extract magic token from URL query parameters
 */
export const extractMagicTokenFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('magicToken');
};

/**
 * Extract email from URL query parameters (optional)
 */
export const extractEmailFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('email');
};

/**
 * Check if a survey supports self-registration
 * Public endpoint - no authentication required
 */
export const checkSurveyEligibility = async (
  surveyId: string
): Promise<CheckSurveyEligibilityResponse> => {
  const response = await fetch(`${API_URL}/self/survey/${surveyId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Survey eligibility check failed: ${response.statusText}`
    );
  }

  const data: CheckSurveyEligibilityResponse = await response.json();
  return data;
};

/**
 * Initiate login flow for existing users
 */
export const loginIntent = async (
  request: LoginIntentRequest
): Promise<LoginIntentResponse> => {
  const payload: LoginIntentRequest = {
    ...request,
    // Backend expects the code without dashes (e.g. NMCSGRGPEDGMPWVH)
    ...(request.parentSurveyAuthCode != null && {
      parentSurveyAuthCode: request.parentSurveyAuthCode.replace(/-/g, ''),
    }),
  };

  const response = await fetch(`${API_URL}/self/auth/login-intent`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    
    // Handle B2C account missing error (409)
    if (response.status === 409 && errorData.code === 'B2C_ACCOUNT_MISSING') {
      throw new B2CAccountMissingError(
        errorData.message || 'Account does not exist. Please sign up first.'
      );
    }
    
    // Ensure error message is properly included for "participant not found" (404)
    const errorMessage = errorData.message || `Login intent failed: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  const data: LoginIntentResponse = await response.json();
  
  // Store session and email for verify step
  storeSession(data.session);
  storeEmail(request.email);
  
  // Note: redirectPath is no longer passed to API, stored in localStorage instead
  
  return data;
};

/**
 * Create new user account (self-signup)
 * Optionally adds user to a survey if addToSurvey is provided
 */
export const createUser = async (
  request: CreateUserRequest
): Promise<CreateUserResponse> => {
  const response = await fetch(`${API_URL}/self/auth/create-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `User creation failed: ${response.statusText}`
    );
  }

  const data: CreateUserResponse = await response.json();
  
  // Store session and email for verify step
  storeSession(data.session);
  storeEmail(request.email);
  
  // Note: redirectPath is no longer passed to API, stored in localStorage instead
  
  return data;
};

/**
 * Debug login using preset password (dev/vendor only)
 * Uses deterministic password format: TmP@rticipant-${email}-A0!
 */
export const loginDebug = async (
  email: string
): Promise<VerifyResponse> => {
  const response = await fetch(`${API_URL}/self/auth/login-debug`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email: email.trim() }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Debug login failed: ${response.statusText}`
    );
  }

  const data: VerifyResponse = await response.json();
  applyVerifyResponse(data);

  return data;
};

/**
 * Verify magic link token and complete authentication
 */
export const verifyMagicLink = async (
  request: VerifyRequest
): Promise<VerifyResponse> => {
  const response = await fetch(`${API_URL}/self/auth/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Verification failed: ${response.statusText}`
    );
  }

  const data: VerifyResponse = await response.json();
  applyVerifyResponse(data);

  // Clear session as it's no longer needed
  clearSession();

  return data;
};

